import React, { useEffect, useState } from "react";
import { X, FileText, Film, Download } from "lucide-react";

/**
 * AttachmentGrid — renders an array of post/comment attachments.
 * Images: masonry 2-col grid (or 1 col when only one) with lightbox.
 * Videos: inline native <video controls>.
 * Files (pdf/docx/etc): chip with download link.
 *
 * Props:
 *   - attachments: [{ kind: "image"|"video"|"file", url, filename, size }]
 *   - "data-testid"
 */
function urlAbs(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  // Backend returns "/api/uploads/xxx" — prefix with REACT_APP_BACKEND_URL
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url}`;
}

export default function AttachmentGrid({ attachments = [], testid = "attachments" }) {
  const [lightbox, setLightbox] = useState(null); // attachment object

  // Lock body scroll while lightbox open
  useEffect(() => {
    if (!lightbox) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [lightbox]);

  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter((a) => a.kind === "image");
  const videos = attachments.filter((a) => a.kind === "video");
  const files = attachments.filter((a) => a.kind === "file" || (a.kind !== "image" && a.kind !== "video"));

  return (
    <div className="mt-3 space-y-3" data-testid={testid}>
      {/* Images — masonry 2-col on sm+ */}
      {images.length > 0 && (
        <div className={`grid gap-2 ${images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid={`${testid}-images`}>
          {images.map((img, idx) => (
            <button
              key={img.url + idx}
              type="button"
              onClick={() => setLightbox(img)}
              className="relative overflow-hidden rounded-lg bg-gray-50 hover:opacity-90 transition-opacity"
              data-testid={`${testid}-image-${idx}`}
            >
              <img
                src={urlAbs(img.url)}
                alt={img.filename || "attachment"}
                className={`w-full h-auto block ${images.length === 1 ? "max-h-[520px] object-contain" : "object-cover aspect-[4/3]"}`}
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {/* Videos */}
      {videos.map((vid, idx) => (
        <video
          key={vid.url + idx}
          src={urlAbs(vid.url)}
          controls
          className="w-full max-h-[420px] rounded-lg bg-black"
          data-testid={`${testid}-video-${idx}`}
        />
      ))}

      {/* Files */}
      {files.length > 0 && (
        <div className="space-y-1.5" data-testid={`${testid}-files`}>
          {files.map((f, idx) => {
            const Icon = (f.kind === "video") ? Film : FileText;
            return (
              <a
                key={f.url + idx}
                href={urlAbs(f.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 bg-gray-50 border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm text-fc-navy hover:border-fc-red transition-colors"
                data-testid={`${testid}-file-${idx}`}
              >
                <Icon size={14} className="text-fc-red flex-shrink-0" />
                <span className="flex-1 truncate font-medium">{f.filename || f.url.split("/").pop()}</span>
                <Download size={12} className="text-[#6B7280]" />
              </a>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid={`${testid}-lightbox`}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
            data-testid={`${testid}-lightbox-close`}
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <img
            src={urlAbs(lightbox.url)}
            alt={lightbox.filename || ""}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
